/** @format */
import { css, keyframes } from 'styled-components';

import { quietText, quietTextSmall } from '../../../typography';

const openTick = keyframes`
    from {
        width: 0;
        margin-left: 0;
        opacity: 0;
    }

    60% {
        width: 1.15em;
        margin-left: var(--layout-spacing-4);
        opacity: 1;
    }

    to {
        width: 1em;
        margin-left: var(--layout-spacing-4);
        opacity: 1;
    }
`;

const drawTick = keyframes`
    to {
        stroke-dashoffset: 0;
    }
`;

const press = keyframes`
    50% {
        transform: scale(0.94);
    }
`;

export const styles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--layout-spacing-8);
    margin-top: var(--layout-spacing-32);
    text-align: center;

    .kit-feedback-question {
        ${quietText}
        color: var(--colors-text-secondary);
    }

    .kit-feedback-beta {
        color: var(--colors-brand-secondary);
        text-transform: uppercase;
    }

    .kit-feedback-actions {
        display: flex;
        gap: var(--layout-spacing-8);
    }

    .kit-feedback-disclaimer {
        ${quietTextSmall}
        color: var(--colors-text-tertiary);
    }

    .kit-feedback-button {
        ${quietText}
        display: inline-flex;
        align-items: center;
        /*
         * Sized to match the journey's own feedback buttons, and deep enough to be
         * noticed — see journey-feedback.styles.ts for why they grew. The two rows
         * sit one section apart on the same page, so they have to stay the same.
         */
        padding: var(--layout-spacing-12) var(--layout-spacing-32);
        border: 0.1rem solid var(--colors-border-primary);
        border-radius: var(--effects-radius-full);
        background: none;
        color: var(--colors-text-secondary);
        cursor: pointer;
        transition-property: background-color, border-color, opacity;
        transition-duration: var(--animations-duration-fast);
        transition-timing-function: var(--animations-easing-ease-out);

        &:hover {
            border-color: var(--colors-brand-secondary);
            background-color: var(--colors-surface-secondary);
        }

        &:active {
            animation: ${press} var(--animations-duration-fast)
                var(--animations-easing-ease-out);
        }
    }

    .kit-feedback-tick {
        width: 0;
        height: 1em;
        margin-left: 0;
        flex: 0 0 auto;
        opacity: 0;
        overflow: visible;
    }

    .kit-feedback-tick-mark {
        stroke: var(--colors-brand-secondary);
        stroke-dasharray: 30;
        stroke-dashoffset: 30;
        transform-origin: center;
    }

    .kit-feedback-button[aria-pressed='true'] {
        border-color: var(--colors-brand-secondary);

        .kit-feedback-tick {
            animation: ${openTick} var(--animations-duration-normal)
                var(--animations-easing-ease-out) both;
        }

        .kit-feedback-tick-mark {
            animation: ${drawTick} var(--animations-duration-normal)
                var(--animations-easing-ease-out) both;
        }
    }

    /*
     * No reduced-motion helper exists in this codebase (verified) — this is a
     * raw @media block, following the precedent at journey-feedback.styles.ts.
     * The end state stays, since the tick is the whole point of the press;
     * only the movement getting there goes away.
     */
    @media (prefers-reduced-motion: reduce) {
        .kit-feedback-button {
            animation: none;
        }

        .kit-feedback-button[aria-pressed='true'] {
            .kit-feedback-tick {
                animation: none;
                width: 1em;
                margin-left: var(--layout-spacing-4);
                opacity: 1;
            }

            .kit-feedback-tick-mark {
                animation: none;
                stroke-dashoffset: 0;
            }
        }
    }
`;
